/**
 * Game — the round state machine and the only module that touches
 * RNG / Economy / Difficulty. Everything visual reacts to bus events
 * or reads this object as a read-only view each frame.
 *
 * States:
 *   idle      — betting UI live, chicken on the sidewalk
 *   starting  — bet locked, waiting for the provably-fair roll
 *   waiting   — mid-round, chicken parked on lane `lane`, GO enabled
 *   hopping   — tween in flight toward lane+1 (input locked)
 *   dead      — squashed; short pause, then back to idle
 *   cashed    — paid out; short pause, then back to idle
 */
import { getDifficulty } from './Difficulty.js';

export const HOP_MS = 380;

export default class Game {
  constructor({ bus, rng, economy }) {
    this.bus = bus;
    this.rng = rng;
    this.economy = economy;

    this.state = 'idle';
    this.difficulty = getDifficulty('easy');
    this.lane = 0;            // 0 = sidewalk, 1..lanes = manholes
    this.deathLane = Infinity;
    this.fairMeta = null;     // seeds/commitment of the live round
  }

  get ladder() {
    return this.difficulty.ladder;
  }

  get multiplier() {
    return this.ladder[this.lane] ?? 1;
  }

  /** Payout if the player cashed out right now. */
  get potential() {
    return this.economy.potential(this.multiplier);
  }

  _setState(state) {
    this.state = state;
    this.bus.emit('game:state', { state, game: this });
  }

  setDifficulty(id) {
    if (this.state !== 'idle') return;
    this.difficulty = getDifficulty(id);
    this.bus.emit('game:difficulty', { difficulty: this.difficulty });
  }

  /** Play pressed. Returns false when the bet can't be placed. */
  async start() {
    if (this.state !== 'idle') return false;
    if (!this.economy.placeBet()) {
      this.bus.emit('economy:rejected', {});
      return false;
    }
    this._setState('starting');
    const { lanes, stepDeathProb } = this.difficulty;
    const { deathLane, meta } = await this.rng.rollRound(lanes, stepDeathProb);
    this.deathLane = deathLane;
    this.fairMeta = meta;
    this.lane = 0;
    this._setState('waiting');
    this.bus.emit('round:start', { difficulty: this.difficulty });
    return true;
  }

  /** GO pressed. Kicks off one hop; resolution arrives in `landed()`. */
  go() {
    if (this.state !== 'waiting') return false;
    const target = this.lane + 1;
    const deadly = target === this.deathLane;
    this._setState('hopping');
    this.bus.emit('chicken:hop', { from: this.lane, to: target, deadly, duration: HOP_MS });
    return true;
  }

  /** Called by the scene when the hop tween finishes. */
  landed() {
    if (this.state !== 'hopping') return;
    const target = this.lane + 1;

    if (target === this.deathLane) {
      const lost = this.economy.lose();
      this.lane = target;
      this._setState('dead');
      this.bus.emit('round:dead', { lane: target, lost });
      this._scheduleReset(2000);
      return;
    }

    this.lane = target;
    this.bus.emit('chicken:landed', {
      lane: this.lane,
      multiplier: this.multiplier,
      potential: this.potential,
    });

    if (this.lane === this.difficulty.lanes) {
      this._settle(); // crossed the whole road — automatic max cash-out
    } else {
      this._setState('waiting');
    }
  }

  /** CASH OUT pressed (only valid once at least one lane is passed). */
  cashOut() {
    if (this.state !== 'waiting' || this.lane < 1) return false;
    this._settle();
    return true;
  }

  _settle() {
    const multiplier = this.multiplier;
    const payout = this.economy.cashOut(multiplier);
    this._setState('cashed');
    this.bus.emit('round:cashout', { multiplier, payout, lane: this.lane });
    this._scheduleReset(2200);
  }

  _scheduleReset(ms) {
    setTimeout(() => {
      this.lane = 0;
      this.deathLane = Infinity;
      this.economy.topUpIfBroke();
      this._setState('idle');
      this.bus.emit('round:reset', {});
    }, ms);
  }
}
